import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { booleanAttribute, Component, inject, input, LOCALE_ID, OnInit, output } from '@angular/core';
import { AppComponentBaseDirective } from '@app/shared/directives/app-component-base.directive';
import { LoginService } from '@app/shared/services/login.service';
import { AppConsts } from '@shared/AppConsts';
import { GuestBookingInfo } from '@shared/models';
import { AccountServiceProxy, API_BASE_URL, BankIdAuthenticateResultModel, CountryOutputDto, CountryServiceProxy, PatientLoginInput, PatientServiceProxy, RegisterInput, SwedishPatientInfoFromCheckbizDto } from '@shared/service-proxies/service-proxies';
import { DateTime } from 'luxon';
import { finalize, map, of, shareReplay, switchMap, take, tap } from 'rxjs';

enum RegularSignInPhase {
  EmailCheckPending,
  EmailExists,
  RegisterPending,
  SocialRegisterPending
};

enum RegisterSteps {
  DoBSsn,
  Account
};

@Component({
  selector: 'nw-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent extends AppComponentBaseDirective implements OnInit {
  appBaseUrl = AppConsts.appBaseUrl;
  under18YearsAllowed = input(false, {
    transform: booleanAttribute
  });

  dismissLoginModal = output();
  loginWithBankId = output();

  private countryService = inject(CountryServiceProxy);
  private apiBaseUrl = inject(API_BASE_URL);
  private http = inject(HttpClient);
  private loginService = inject(LoginService);
  private patientService = inject(PatientServiceProxy);
  private accountService = inject(AccountServiceProxy);

  public appLocale = inject(LOCALE_ID);

  nationalityId?: number;
  nationalities$ = this.countryService.getAll().pipe(map(result => result.items!), shareReplay(1));
  selected$ = this.nationalities$.pipe(map(items => items.reduce((acc, curr) => ({
    ...acc,
    [curr.id]: curr
  }), {} as Record<number, CountryOutputDto>)));

  selectedDefault = {} as Record<number, CountryOutputDto>;
  externalAuthProviders = [] as any[];
  regularSignInPhase = RegularSignInPhase;
  currentRegularSignInPhase = RegularSignInPhase.EmailCheckPending;
  currentRegisterStep = RegisterSteps.DoBSsn;
  registerSteps = RegisterSteps;
  loginOptions = [
    { value: 'BankID', label: 'LogInWithBankId' },
    { value: 'Regular', label: 'LogInWithoutBankId'}
  ];
  selectedLoginOption = 'BankID';
  accountChecked = false;
  email = '';
  password = '';
  authenticating = false;
  registerInput = new RegisterInput();
  ssnLastDigits = '';
  confirmPassword?: string;
  genders: Array<{label: string, value: number}> = [
    {
      label: this.l('Male'),
      value: 1
    },
    {
      label: this.l('Female'),
      value: 2
    }
  ];
  registering = false;
  countries: CountryOutputDto[] = [];

  get isUnder18YearsOld(): boolean {
    return this.registerInput.dateOfBirth && DateTime.utc().diff(this.registerInput.dateOfBirth, 'years').years < 18;
  }

  ngOnInit(): void {
    this.resolveCountries();
    this.getDefaultCountry();
  }

  resolveCountries() {
    this.nationalities$.pipe(take(1)).subscribe(result => {
      this.countries = result;
    });
  }

  getDefaultCountry() {
    this.countryService.getIpCountryOfRequest().subscribe(country => {
      this.nationalityId = country.id;
    });
  }

  onNationalityChange(id: number) {
    const sweden = 2;
    if(id === sweden) {
      this.selectedLoginOption = 'BankID';
    } else this.selectedLoginOption = 'Regular';
  }

  reset() {
    this.regularSignInPhase = RegularSignInPhase;
    this.currentRegularSignInPhase = RegularSignInPhase.EmailCheckPending;
    this.currentRegisterStep = RegisterSteps.DoBSsn;
    this.accountChecked = false;
    this.email = '';
    this.authenticating = false;
    this.registerInput = new RegisterInput();
  }

  checkForEmail(valid: boolean) {
    this.accountChecked = true;
    if (!valid) return;

    const url = `${this.apiBaseUrl}/api/PatientTokenAuth/Authenticate`;
    this.authenticating = true;
    this.http.post(url, JSON.stringify(new PatientLoginInput({
      email: this.email,
      password: '***'
    })), {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    }).pipe(
      finalize(() => {
        this.authenticating = false;
      })
    ).subscribe({
      error: (response: HttpErrorResponse) => {
        // We're expecting error here, either invalid password, or account doesn't exist
        if (response.error.error.message === 'Invalid password supplied')
          this.currentRegularSignInPhase = RegularSignInPhase.EmailExists;
        else {
          this.registerInput = new RegisterInput();
          this.registerInput.countryId = this.nationalityId!;
          this.registerInput.email = this.email;
          this.currentRegularSignInPhase = RegularSignInPhase.RegisterPending;
        }
      }
    });
  }

  onSSN() {
    if(!this.ssnLastDigits || !this.registerInput.dateOfBirth) {
        this.registerInput.ssn = undefined;
    } else this.registerInput.ssn = `${this.registerInput.dateOfBirth.toFormat('yyyyLLdd')}-${this.ssnLastDigits}`;
  }

  onDateOfBirthComplete(ssnLastDigitsInputId: string) {
    const element = document.getElementById(ssnLastDigitsInputId);
    const sweden = 2;
    if(element && this.registerInput.countryId === sweden) {
      element.focus();
    }
  }

  registerPrev() {
    if(this.currentRegisterStep === RegisterSteps.DoBSsn) {
        this.currentRegularSignInPhase = RegularSignInPhase.EmailCheckPending;
        return;
    }
    this.currentRegisterStep--;
  }

  registerNext() {
    const sweden = 2;
    if (this.currentRegisterStep === RegisterSteps.Account) return;

    if (this.currentRegisterStep === RegisterSteps.DoBSsn) {
        const ageInDays = DateTime.utc().diff(this.registerInput.dateOfBirth, 'days').days;

        if (ageInDays <= 365 * 18 && !this.under18YearsAllowed()) {
          this.message.error(this.l('PatientsUnder18RegisterError'));
          return;
        }

        if(this.registerInput.countryId === sweden) {
            this.patientService
            .getGetSwedishPatientFromSearch(this.registerInput.dateOfBirth, this.registerInput.ssn)
            .pipe(finalize(() => this.currentRegisterStep++))
            .subscribe((result) => {
              this.assignFoundPatientForRegister(result);
            });
        } else {
            this.currentRegisterStep++;
        }
    } else {
        this.currentRegisterStep++;
    }
  }

  assignFoundPatientForRegister(result: SwedishPatientInfoFromCheckbizDto) {
    if(!result) return;
    this.registerInput.firstname = result.name!;
    this.registerInput.lastname = result.surname!;
    this.registerInput.address = result.address;
    this.registerInput.city = result.city;
    this.registerInput.zipCode = result.zipCode;
    this.registerInput.genderId = result.genderId;
  }

  handleAddressChange(event: google.maps.places.PlaceResult, input: HTMLInputElement) {
    const locality = event.address_components?.find(c => c.types?.includes('locality'));
    const admLvl1 = event.address_components?.find(c => c.types?.includes('administrative_area_level_1'));
    const city = locality || admLvl1;
    const zipCodeComponent = event.address_components?.find(c => c.types?.includes('postal_code'));
    const countryComponent = event.address_components?.find(c => c.types?.includes('country'));
    const address = input.value;
    this.registerInput.address = address;
    if(city) {
      this.registerInput.city = city.long_name;
    }

    if(zipCodeComponent) {
      this.registerInput.zipCode = zipCodeComponent.short_name;
    }
    if(countryComponent) {
      const country = this.countries.find(c => c.shortName!.toLowerCase() === countryComponent.short_name.toLowerCase());
      this.registerInput.residenceCountryId = country?.id;
    }
  }

  tryRegister(valid: boolean) {
    if (!valid) return;
    this.registering = true;
    this.registerInput.isUnder18YearsAllowed = this.under18YearsAllowed();
    if (this.under18YearsAllowed() && this.isUnder18YearsOld) {
      this.registerInput.password = 'Dummy@Xyz/Password_103';
    }

    this.accountService.register(this.registerInput).pipe(
      switchMap(result => {
        if (result.canLogin) {
          return this.loginService.login(new PatientLoginInput({
            email: this.registerInput.email,
            password: this.registerInput.password
          }));
        } else if (result.under18Years && result.patientId) {
          this.message.warn(this.l('PatientsUnder18WithoutAccountBooking_message'));
          return of(false);
        }
        else {
          this.message.warn(this.l('PleaseConfirmYourEmailAndThenProceedFromHere'));
          this.email = this.registerInput.email;
          this.currentRegularSignInPhase = RegularSignInPhase.EmailExists;

          return of(false);
        }
      }),
      finalize(() => {
        this.registering = false;
      })
    ).subscribe(result => {
      if (result) {
        this.dismissLoginModal.emit();
      }
    });
  }

  login(valid: boolean) {
    if (valid) {
      console.log('Login successful');
    } else {
      console.log('Login failed');
    }
    const {
      email,
      password
    } = this;
    this.authenticating = true;
    this.loginService.login(new PatientLoginInput({
      email,
      password
    })).pipe(
      tap(() => {
        this.authenticating = false;
      })
    ).subscribe(isSuccess => {
      if (isSuccess) {
        this.dismissLoginModal.emit();
      }
    });
  }

  bankIdSignIn(data: BankIdAuthenticateResultModel) {
    this.loginService.processBankIdLoginResult(data);
  }
}
